'use client';

import { Canvas } from '@react-three/fiber';
import { CameraControls, Stars, PerspectiveCamera } from '@react-three/drei';
import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import UniverseSystem from './UniverseSystem';
import ProjectModal from './ProjectModal';
import Navigation from './Navigation';
import SpaceshipAnimation from './SpaceshipAnimation';
import HeroSection from './HeroSection';
import { Project } from '@/types/project';

// Dados dos projetos (universos) - estilo monocromático
const projects: Project[] = [
  {
    id: 1,
    name: "E-Commerce Galáctico",
    description: "Plataforma de e-commerce completa com sistema de pagamentos integrado e dashboard administrativo avançado. Desenvolvido com as melhores práticas de UX/UI.",
    technologies: ["Laravel", "React", "PostgreSQL", "Redis", "Stripe"],
    mainTech: "Laravel",
    image: "https://images.pexels.com/photos/230544/pexels-photo-230544.jpeg?auto=compress&cs=tinysrgb&w=800",
    demoUrl: "#",
    repoUrl: "#",
    position: [0, 0, 0],
    color: "#ffffff"
  },
  {
    id: 2,
    name: "Sistema de Gestão Estelar",
    description: "ERP completo para gestão empresarial com módulos de vendas, estoque, financeiro e recursos humanos. Interface intuitiva e relatórios avançados.",
    technologies: ["FastAPI", "Vue.js", "MongoDB", "Docker", "AWS"],
    mainTech: "FastAPI",
    image: "https://images.pexels.com/photos/590016/pexels-photo-590016.jpeg?auto=compress&cs=tinysrgb&w=800",
    demoUrl: "#",
    repoUrl: "#",
    position: [15, 5, -10],
    color: "#cccccc"
  },
  {
    id: 3,
    name: "Portal Cósmico de Notícias",
    description: "CMS avançado para publicação de conteúdo com sistema de comentários, tags e busca inteligente. SEO otimizado e performance excepcional.",
    technologies: ["Next.js", "Supabase", "TypeScript", "Tailwind", "Vercel"],
    mainTech: "Next.js",
    image: "https://images.pexels.com/photos/518543/pexels-photo-518543.jpeg?auto=compress&cs=tinysrgb&w=800",
    demoUrl: "#",
    repoUrl: "#",
    position: [-12, -8, 5],
    color: "#888888"
  },
  {
    id: 4,
    name: "App Mobile Intergaláctico",
    description: "Aplicativo mobile híbrido para delivery com geolocalização, pagamentos e chat em tempo real. Experiência nativa em iOS e Android.",
    technologies: ["React Native", "Node.js", "Socket.io", "Firebase", "Expo"],
    mainTech: "React Native",
    image: "https://images.pexels.com/photos/147413/twitter-facebook-together-exchange-of-information-147413.jpeg?auto=compress&cs=tinysrgb&w=800",
    demoUrl: "#",
    repoUrl: "#",
    position: [8, -12, 8],
    color: "#666666"
  },
  {
    id: 5,
    name: "Dashboard Analítico Espacial",
    description: "Plataforma de analytics com visualizações interativas, relatórios automatizados e insights em tempo real. Machine Learning integrado.",
    technologies: ["Python", "Django", "D3.js", "PostgreSQL", "Celery"],
    mainTech: "Django",
    image: "https://images.pexels.com/photos/590022/pexels-photo-590022.jpeg?auto=compress&cs=tinysrgb&w=800",
    demoUrl: "#",
    repoUrl: "#",
    position: [-18, 3, -15],
    color: "#444444"
  }
];

export default function GalacticPortfolio() {
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isNavigating, setIsNavigating] = useState(false);
  const [showHero, setShowHero] = useState(true);
  const controlsRef = useRef<any>(null);

  const handleProjectClick = (project: Project) => {
    setIsNavigating(true);
    
    // Animar câmera para o projeto com movimento suave
    if (controlsRef.current) {
      const targetPosition = [
        project.position[0] + 8,
        project.position[1] + 8,
        project.position[2] + 8
      ];
      
      controlsRef.current.setLookAt(
        targetPosition[0],
        targetPosition[1],
        targetPosition[2],
        project.position[0],
        project.position[1],
        project.position[2],
        true
      );
    }
    
    // Aguardar animação da câmera antes de abrir modal
    setTimeout(() => {
      setIsNavigating(false);
      setSelectedProject(project);
    }, 2500);
  };

  const handleNavigateToProject = (project: Project) => {
    setShowHero(false);
    handleProjectClick(project);
  };

  const handleExploreUniverse = () => {
    setShowHero(false);
  };

  // Efeito para criar partículas de estrelas
  useEffect(() => {
    const createParticles = () => {
      const particles = document.querySelectorAll('.particle');
      particles.forEach(particle => particle.remove());

      // Reduzir número de partículas em mobile
      const particleCount = window.innerWidth < 768 ? 50 : 100;
      
      for (let i = 0; i < particleCount; i++) {
        const particle = document.createElement('div');
        particle.className = `particle particle-${Math.floor(Math.random() * 3) + 1}`;
        particle.style.left = Math.random() * 100 + '%';
        particle.style.top = Math.random() * 100 + '%';
        particle.style.animationDelay = Math.random() * 4 + 's';
        document.body.appendChild(particle);
      }
    };

    if (!showHero) {
      createParticles();
    }
    
    return () => {
      const particles = document.querySelectorAll('.particle');
      particles.forEach(particle => particle.remove());
    };
  }, [showHero]);

  return (
    <div className="relative w-full h-screen bg-black overflow-hidden">
      {/* Hero Section */}
      <AnimatePresence>
        {showHero && (
          <HeroSection onExplore={handleExploreUniverse} />
        )}
      </AnimatePresence>

      {/* Canvas 3D - sempre renderizado mas oculto durante hero */}
      <div className={`absolute inset-0 ${showHero ? 'opacity-0' : 'opacity-100'} transition-opacity duration-1000`}>
        <Canvas>
          <PerspectiveCamera makeDefault position={[0, 0, 30]} />
          <CameraControls
            ref={controlsRef}
            minDistance={10}
            maxDistance={100}
            enabled={!showHero}
          />
          
          {/* Ambiente espacial */}
          <Stars
            radius={300}
            depth={60}
            count={window.innerWidth < 768 ? 8000 : 12000}
            factor={8}
            saturation={0}
            fade={true}
          />
          
          {/* Iluminação monocromática */}
          <ambientLight intensity={0.05} />
          <pointLight position={[0, 0, 0]} intensity={0.8} color="#ffffff" />
          <directionalLight position={[10, 10, 10]} intensity={0.3} color="#ffffff" />
          
          {/* Sistemas de universos (projetos) */}
          {projects.map((project) => (
            <UniverseSystem
              key={project.id}
              project={project}
              onClick={() => handleProjectClick(project)}
            />
          ))}
        </Canvas>
      </div>

      {/* Animação da nave espacial - oculta em mobile */}
      <div className="hidden sm:block">
        <SpaceshipAnimation />
      </div>

      {/* Navegação */}
      {!showHero && (
        <Navigation
          projects={projects}
          onProjectSelect={handleNavigateToProject}
          selectedProject={selectedProject}
        />
      )}

      {/* Modal do projeto */}
      <AnimatePresence>
        {selectedProject && (
          <ProjectModal
            project={selectedProject}
            onClose={() => setSelectedProject(null)}
          />
        )}
      </AnimatePresence>

      {/* Overlay de navegação */}
      <AnimatePresence>
        {isNavigating && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-40 px-4"
          >
            <div className="text-center">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                className="w-16 sm:w-20 h-16 sm:h-20 border-4 border-white/10 border-t-white rounded-full mx-auto mb-4 sm:mb-6"
              />
              <motion.p
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="text-white text-lg sm:text-xl font-light tracking-wider"
              >
                Navegando pelo cosmos...
              </motion.p>
              <motion.div
                className="flex justify-center space-x-1 mt-3 sm:mt-4"
                animate={{ opacity: [0.3, 1, 0.3] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                {[...Array(5)].map((_, i) => (
                  <div
                    key={i}
                    className="w-1 h-1 bg-white rounded-full"
                    style={{ animationDelay: `${i * 0.2}s` }}
                  />
                ))}
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Instruções - adaptadas para mobile */}
      {!showHero && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute bottom-4 sm:bottom-6 left-4 sm:left-6 text-white/40 text-xs sm:text-sm font-light max-w-xs sm:max-w-none"
        >
          <p className="hidden sm:block">🖱️ Arraste para navegar • 🔍 Scroll para zoom • ⭐ Clique nos universos</p>
          <p className="sm:hidden">👆 Toque para navegar • ⭐ Toque nos universos</p>
        </motion.div>
      )}
    </div>
  );
}